import { useEffect, useRef } from 'react';

const removalSteps = [
  {
    num: '01',
    title: '사전 준비',
    desc: '석면조사 결과 검토, 작업계획서 작성, 관할 기관 신고, 작업자 안전교육 실시',
  },
  {
    num: '02',
    title: '작업장 격리',
    desc: '비닐 시트로 음압 격리 구역 설치, 음압기 가동, 출입 통제 시스템 운영',
  },
  {
    num: '03',
    title: '석면 습식 제거',
    desc: '습식 공법으로 석면 자재를 신중히 제거하여 비산을 최소화, 전동 공구 사용 제한',
  },
  {
    num: '04',
    title: '청정 작업',
    desc: 'HEPA 진공 청소기 사용, 물 세척 등으로 잔여 석면 섬유를 완전히 제거',
  },
  {
    num: '05',
    title: '적법 폐기',
    desc: '석면 함유 폐기물을 법정 기준에 따라 밀봉 포장 후 지정 폐기물 업체 위탁 처리',
  },
];

const features = [
  { icon: 'ri-shield-check-line', title: '음압 격리 시스템', desc: '최첨단 음압 격리 장비로 작업 구역 완전 밀폐' },
  { icon: 'ri-user-star-line', title: '자격 보유 전문가', desc: '석면해체·제거업 허가 보유 전문 인력 투입' },
  { icon: 'ri-recycle-line', title: '석면 폐기물 처리', desc: '지정 폐기물 처리업체를 통한 적법한 최종 처리' },
  { icon: 'ri-file-list-3-line', title: '완료 보고서 제공', desc: '전 과정 기록 및 완료 후 상세 보고서 제출' },
];

export default function RemovalSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-fade-in').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="removal" className="py-24 bg-green-950 text-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="section-fade-in mb-16">
          <span className="inline-block bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
            Removal Process
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight whitespace-nowrap">
              석면 해체·제거
            </h2>
            <p className="text-white/60 text-base max-w-md leading-relaxed">
              법적 기준을 완벽히 준수하는 체계적인 5단계 공정으로
              안전하고 신속하게 석면을 해체·제거합니다.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* 이미지 — PC만 표시 */}
          <div className="section-fade-in hidden lg:block">
            <div className="relative rounded-3xl overflow-hidden h-96">
              <img
                src="https://public.readdy.ai/ai/img_res/049053bed845fcf217d1c8bf5183ecfe.jpg"
                alt="석면 해체제거 작업"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-950/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { val: '음압 격리', sub: 'Negative Pressure' },
                    { val: '습식 공법', sub: 'Wet Method' },
                    { val: 'HEPA 청소', sub: 'HEPA Vacuum' },
                  ].map((item) => (
                    <div key={item.val} className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center border border-white/20">
                      <div className="text-xs font-bold text-amber-300">{item.val}</div>
                      <div className="text-xs text-white/60 mt-0.5">{item.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 스텝 — 모바일에서 전체 너비 */}
          <div className="section-fade-in flex flex-col gap-4 lg:col-auto col-span-full">
            {removalSteps.map((step, i) => (
              <div
                key={step.num}
                className="section-fade-in flex items-start gap-4 group"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-amber-500/20 border border-amber-500/30 rounded-xl group-hover:bg-amber-500/30 transition-all duration-200">
                  <span className="text-amber-400 text-xs font-bold">{step.num}</span>
                </div>
                <div className="pt-1">
                  <div className="font-bold text-white mb-1">{step.title}</div>
                  <p className="text-sm text-white/60 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="section-fade-in">
          <div className="border-t border-white/10 pt-12">
            <h3 className="text-xl font-bold text-white mb-8">서비스 특징</h3>
            {/* PC: 4열, 모바일: 2x2 */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map((feature, i) => (
                <div
                  key={feature.title}
                  className="section-fade-in bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6 hover:bg-white/10 transition-all duration-200"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-xl mb-4">
                    <i className={`${feature.icon} text-amber-400 text-lg`}></i>
                  </div>
                  <div className="font-semibold text-white text-sm mb-2">{feature.title}</div>
                  <p className="text-xs text-white/50 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
